import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Envs,
  ErrorDisplayType,
  ErrorInfo,
  EventType,
  MenuOptions,
  Modules,
  deviceUtils,
  useConfig,
  useEnvContext,
  useEventBusEmit,
  useGlobalErrorStore,
  useMenuStore,
  useRouterHistory,
} from 'scbk-services';

import { Dialog, ErrorPopupComponent, usePageMove, usePopup } from 'scbk-shared-module';

const ERROR_ACTION = {
  MAIN: 'MAIN',
  BACK: 'BACK',
} as const;

/**
 * 글로벌 오류 컴포넌트
 * @returns
 */
const GlobalErrorComponents: React.FC = () => {
  const { getEnv } = useEnvContext();
  // 다국어 처리
  const { t } = useTranslation();

  const errorPopup = usePopup('ErrorPopupComponent');
  const errorRef = useRef<ErrorInfo | undefined>(undefined);
  /* 2026-08-28 - 웹접근성 Focus 처리 */
  const activeElement = useRef<HTMLElement | undefined>(undefined);

  const { errorInfo, clear: errorClear } = useGlobalErrorStore();
  const { getConfig } = useConfig();
  const { findByPath } = useMenuStore(); // menuStore url로 menu정보 조회
  const routerHistorySize = useRouterHistory((state) => state.size); // routerHistory state

  const emit = useEventBusEmit();
  // main 으로 이동
  const { goMain } = usePageMove();

  const getHistorySize = useCallback(() => {
    if (typeof routerHistorySize === 'function') {
      return routerHistorySize();
    }
    return routerHistorySize ?? 0;
  }, [routerHistorySize]);

  /**
   * error 출력 후 CTA 버튼 클릭 이벤트 시 필요한 값들 정제
   */
  const composeErrorInfo = useMemo(() => {
    if (!errorInfo) {
      return undefined;
    }

    let errorMenuId = errorInfo.errorMenuId ?? errorInfo.nextPage ?? '';
    let errorMenuParameters = errorInfo.errorMenuParameters ?? errorInfo.nextPageParameters ?? {};
    const clearHistory = errorInfo.clearHistory ?? false;

    if (_.isEmpty(errorMenuId) && errorInfo.displayType === ErrorDisplayType.PAGE) {
      if (deviceUtils.isApp()) {
        // 네이티브인 경우
        errorMenuId = ERROR_ACTION.BACK;
      } else {
        // PC or MobileWeb
        errorMenuId = ERROR_ACTION.MAIN;
      }
    }

    return {
      ...errorInfo,
      errorMenuId,
      errorMenuParameters,
      clearHistory,
    };
  }, [errorInfo]);

  /**
   * 오류 화면 확인버튼 클릭 이벤트 처리
   */
  const handleConfirm = useCallback(() => {
    const error = composeErrorInfo;
    if (!error) {
      return;
    }

    //에러모달을 닫는다.
    errorClear();
    errorRef.current = undefined;

    const { errorMenuId, errorMenuParameters = {}, onConfirm } = error;

    if (errorMenuId) {
      if (errorMenuId === ERROR_ACTION.MAIN) {
        goMain();
        return;
      }

      if (errorMenuId === ERROR_ACTION.BACK) {
        if (errorPopup.isOpen) {
          errorPopup.close();
        }
        //router.back();
        emit(EventType.RouteBackRequest);
        return;
      }

      const options = {
        replace: false,
        routeData: errorMenuParameters ?? {},
        clearStateData: false,
      } as MenuOptions;

      emit(EventType.MenuNavigationRequest, errorMenuId, { ...options });
      return;
    }

    if (onConfirm) {
      // 팝업 종료시 필요한 callback 직접 지정 가능
      onConfirm();
    }
  }, [errorClear, composeErrorInfo, errorPopup, emit]);

  /**
   * 오류 MODAL 오픈
   */
  const handleOpenErrorConfirm = useCallback(async () => {
    const tempError = errorInfo;
    if (!tempError) {
      return;
    }

    // errorClear();

    const { code, errorMessage, errorGuideMessage, errorModule } = tempError;

    console.log('### errorGuideMessage : ', errorGuideMessage);

    // TODO: 다국어 처리
    // confirm 버튼 label 특정 오류 코드에 대한 처리
    let okBtnLabel = t('GlobalErrorComponent.001', '확인');
    let cancelBtnLabel = t('GlobalErrorComponent.002', '취소');

    // PRCLGN0015 : 로그인이 필요합니다.
    // PRCCMM0050 : ID/PWD 로그인 후 서명 필요한 서비스 접근 체크(acCheckType = 03)
    // PRCCMM0052 : ID/PWD 로그인 사용자 상품 신규 메뉴 접근 통제(acCheckType = 05)
    if (code === 'PRCLGN0015') {
      okBtnLabel = t('GlobalErrorComponent.003', '로그인');
    }
    //이체성거래 단말지정서비스 체크(acCheckType = 04)
    else if (code === 'PRCCMM0045' || code === 'PRCCMM0050' || code === 'PRCCMM0052') {
      okBtnLabel = t('GlobalErrorComponent.004', '예');
      cancelBtnLabel = t('GlobalErrorComponent.005', '아니오');
    }

    let errCode = code;
    if (!_.isEmpty(errorModule)) {
      errCode += ` - ${errorModule}`;
    }

    const isOk = await Dialog.confirm({
      title: '',
      errCode,
      content: errorMessage,
      guideDesc: errorGuideMessage,
      okBtnLabel,
      cancelBtnLabel,
    });

    if (isOk) {
      handleConfirm();

      /* 2026-08-28 - 웹접근성 Focus 처리 */
      if (code) {
        requestAnimationFrame(() => {
          console.log('errorPopup effect active confirm yes ,', activeElement.current);
          activeElement.current?.focus();
        });
      }
    } else {
      errorClear();
      errorRef.current = undefined;

      /* 2026-08-28 - 웹접근성 Focus 처리 */
      if (code) {
        requestAnimationFrame(() => {
          console.log('errorPopup effect active confirm no ,', activeElement.current);
          activeElement.current?.focus();
        });
      }
    }
  }, [errorInfo, handleConfirm, errorClear]);

  /**
   * 오류 Confirm 오픈
   */
  const handleOpenErrorModal = useCallback(async () => {
    const tempError = errorInfo;
    if (!tempError) {
      return;
    }

    // errorClear();

    const { code, errorMessage, errorGuideMessage, errorModule } = tempError;

    console.log('errorGuideMessage : ', errorGuideMessage);

    let errCode = code;
    if (!_.isEmpty(errorModule)) {
      errCode += ` - ${errorModule}`;
    }

    const isOk = await Dialog.alert({
      title: '',
      errCode,
      content: errorMessage,
      guideDesc: errorGuideMessage,
      okBtnLabel: t('GlobalErrorComponent.006', '확인'),
    });

    if (isOk) {
      handleConfirm();

      /* 2026-08-28 - 웹접근성 Focus 처리 */
      if (code) {
        requestAnimationFrame(() => {
          console.log('errorPopup effect active ,', activeElement.current);
          activeElement.current?.focus();
        });
      }
    }
  }, [errorInfo, handleConfirm]);

  /**
   * error page로 이동하는 로직 : commonConfig에 있는 path 기준으로 menu를 뽑아 error page로 이동
   */
  const goErrorPage = useCallback(
    async (errorRoute: string, options?: MenuOptions) => {
      try {
        const url: string = errorRoute ?? '';

        const menu = findByPath(url);
        if (!menu) {
          if (getEnv() === Envs.LOCAL || getEnv() === Envs.DEV) {
            alert('[DEV] 메뉴정보가 없습니다.');
          }
          return;
        }

        // const screenId: string = errorRoute ?? '';

        const historySize = getHistorySize();

        const opt = {
          ...options,
          replace: historySize !== 0,
        };
        emit(EventType.MenuNavigationRequest, menu.id, { ...opt });
        // emit(EventType.RouteRequest, screenId, { ...opt });
      } catch (e) {
        console.error(e);
      }
    },
    [findByPath, getHistorySize, emit, getEnv],
  );

  useEffect(() => {
    if (!errorInfo) {
      errorRef.current = undefined;
      return;
    }

    if (errorRef.current && errorRef.current === errorInfo) {
      return;
    }

    errorRef.current = errorInfo;

    // Error Display Type PAGE 방식 에러 페이지로 이동한다.
    if (ErrorDisplayType.PAGE === errorInfo.displayType) {
      // 500 에러면 500에러 페이지 / 이외는 일반 에러 페이지로 이동한다.
      const tempError = { ...composeErrorInfo };
      const errorCode = tempError?.code;
      let errorRoute =
        errorCode === 'HTTP_ERROR_500'
          ? (getConfig(Modules.COMMON)?.error500Route ?? '')
          : (getConfig(Modules.COMMON)?.errorRoute ?? '');

      // 이용시간 에러
      if (errorCode === 'PRCCMM0027' || errorCode === 'PRCCMM0028' || errorCode === 'PRCCMM0054') {
        errorRoute = getConfig(Modules.COMMON)?.errorSvcTimeRoute ?? '';
      }

      // 경제제재국가 접근 금지 안내 에러
      if (errorCode === 'PRCCMM0077') {
        errorRoute = getConfig(Modules.COMMON)?.errorRestrictCountryRoute ?? '';
      }

      console.log('# ErrorCom goPage ', tempError);
      //에러페이지 이동
      goErrorPage(errorRoute, {
        routeData: {
          title: tempError.title,
          code: tempError.code,
          errorMessage: tempError?.errorMessage ?? '',
          errorGuideMessage: tempError?.errorGuideMessage ?? '',
          onConfirm: tempError?.onConfirm,
          errorModule: tempError?.errorModule,
          errorLocation: tempError?.errorLocation,
          errorMenuId: tempError?.errorMenuId,
          errorMenuParameters: tempError?.errorMenuParameters,
          clearHistory: tempError?.clearHistory ?? false,
        },
      });
      return;
    }

    /* 2026-08-28 - 웹접근성 Focus 처리 */
    activeElement.current = tempRef.current;
    console.log('errorPopup effect > ', activeElement.current);
    console.log('errorPopup effect errorInfo >', errorInfo);

    // Error Display Type POPUP open
    if (ErrorDisplayType.POPUP === errorInfo.displayType) {
      // error 팝업 open
      errorPopup.setTrigger(activeElement.current as HTMLElement);
      errorPopup.open();
      return;
    }

    // Error Display Type MODAL 방식 Confirm을 오픈해준다.
    if (ErrorDisplayType.CONFIRM === errorInfo.displayType) {
      // Confirm open
      handleOpenErrorConfirm();
      return;
    }

    // Error Display Type MODAL 방식 Alert을 오픈해준다.
    // MODAL open || || DEFAULT
    handleOpenErrorModal();
    return;
  }, [
    errorInfo,
    composeErrorInfo,
    goErrorPage,
    errorPopup,
    handleOpenErrorConfirm,
    handleOpenErrorModal,
  ]);

  const tempRef = useRef<HTMLElement | undefined>(undefined);

  useEffect(() => {
    const saveTarget = (e: Event) => {
      if (e.target instanceof HTMLElement) {
        tempRef.current = (e.target.closest('button') as HTMLElement) ?? undefined;
      }
    };

    const handleKeydown = (e: KeyboardEvent) => {
      if (e.key !== 'Enter') return;
      saveTarget(e);
    };

    document.addEventListener('pointerdown', saveTarget);
    document.addEventListener('keydown', handleKeydown);

    return () => {
      document.removeEventListener('pointerdown', saveTarget);
      document.removeEventListener('keydown', saveTarget);
    };
  }, []);

  if (!errorInfo) {
    return null;
  }

  return (
    <>
      {errorPopup.isOpen && (
        <ErrorPopupComponent
          isOpen={errorPopup.isOpen}
          setOpen={(open) => {
            if (!open) {
              errorPopup.close();
              errorClear();
              errorRef.current = undefined;

              /* 2026-08-28 - 웹접근성 Focus 처리 */
              if (errorInfo?.code) {
                requestAnimationFrame(() => {
                  console.log('errorPopup effect active ,', activeElement.current);
                  activeElement.current?.focus();
                });
              }
            }
          }}
          onComplete={() => {
            errorPopup.close();
            handleConfirm();

            /* 2026-08-28 - 웹접근성 Focus 처리 */
            if (errorInfo?.code) {
              requestAnimationFrame(() => {
                console.log('errorPopup effect active ,', activeElement.current);
                activeElement.current?.focus();
              });
            }
          }}
          errorInfo={errorInfo}
        />
      )}
    </>
  );
};

export { GlobalErrorComponents };
